import React, { useContext, useState, useEffect, useRef, useMemo } from 'react'
import AuthContext from '../../context/AuthContext'
import { Breadcrumb, BreadcrumbItem, Tile } from 'carbon-components-react'
import Toast from '../../components/Notification/Toast'
import { CurrenciesList } from './currencies.tsx';

const { REACT_APP_DEV_API_URL, REACT_APP_PROD_API_URL, REACT_APP_DEV_MEDIA_URL, REACT_APP_PROD_MEDIA_URL, NODE_ENV } = process.env;
const API_URL =
  NODE_ENV === 'production' ? process.env.REACT_APP_PROD_API_URL : process.env.REACT_APP_DEV_API_URL;
const MEDIA_URL =
  NODE_ENV === 'production' ? process.env.REACT_APP_PROD_MEDIA_URL : process.env.REACT_APP_DEV_MEDIA_URL;

const CurrenciesSettings = ({ match }) => {
  // Variables for pagination
  const [totalItems, setTotalItems] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [firstRowIndex, setFirstRowIndex] = useState(0);

  // Variables for modals and toasts
  const [list, setList] = useState([]);
  let toastProperties = null;
  let [openAddModal, setOpenAddModal] = useState(false)
  let [openEditModal, setOpenEditModal] = useState(false)
  let [openDeleteModal, setOpenDeleteModal] = useState(false)
  let [editID, setEditID] = useState(null)
  let [deleteID, setDeleteID] = useState(null)
  let [error, setError] = useState(null)

  // Other Variables
  let { user, authTokens } = useContext(AuthContext)
  let [currencies, setCurrencies] = useState([])

  // Variables for Search
  const [currencySearch, setCurrencySearch] = useState("");

  const addForm = useRef();
  const editForm = useRef();

  useEffect(() => {
    const getCurrencies = async () => {
      let response = await fetch(`${API_URL}/system/financial/currencies/`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + authTokens?.access
        }
      })
      let data = await response.json()
      setCurrencies(data)
    }
    getCurrencies()
    setTotalItems(currencies?.length)
    document.title = "Currencies Settings | WillowBMS"
  }, [])

  const showToast = (type, title, description) => {
    const id = Math.floor((Math.random() * 100) + 1);
    const time = new Date().toLocaleString();

    switch (type) {
      case 'success':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'success',
          time: time
        }
        break;
      case 'error':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'error',
          time: time
        }
        break;
      case 'info':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'info',
          time: time
        }
        break;
      case 'warning':
        toastProperties = {
          id,
          title: title,
          description: description,
          kind: 'warning',
          time: time
        }
        break;
      default:
        setList([]);
    }
    setList([...list, toastProperties]);
  }

  let getCurrencyList = async () => {
    let response = await fetch(`${API_URL}/system/financial/currencies/`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authTokens?.access
      }
    })
    let data = await response.json()
    setCurrencies(data)
  }

  let addCurrency = async (values) => {
    values.preventDefault()

    let response = await fetch(`${API_URL}/system/financial/currencies/add/`, {
      method: "POST",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authTokens?.access
      },
      body: JSON.stringify({
        'currency': values.target.currency.value,
        'currencycode': values.target.currencycode.value
      })
    })

    if (response.status === 200) {
      getCurrencyList()
      setOpenAddModal(false)
      showToast("success", "Success", "System currency has been added.")
      setError(false)
      document.getElementById("addCurrency").reset()
    } else {
      setError(true)
    }
  }

  let editCurrency = async (values) => {
    values.preventDefault();
    let response = await fetch(`${API_URL}/system/financial/currency/edit/${editID.id}/`, {
      method: "PUT",
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authTokens?.access
      },
      body: JSON.stringify({
        'currency': values.target.currency.value,
        'currencycode': values.target.currencycode.value,
      })
    })

    if (response.status === 200) {
      getCurrencyList()
      setOpenEditModal(false)
      showToast("success", "Success", "System currency has been edited.")
      setError(false)
    } else {
      setError(true)
    }
  }

  let editSetup = async (id) => {
    currencies?.map((currency) => { currency.id === id && setEditID(currency) }
    )
  }

  let batchDelete = async (selectedCurrencies) => {
    selectedCurrencies.map((currency) => deleteCurrency(currency.id))
}

  let deleteCurrency = async (currencyID) => {
    let response = await fetch(`${API_URL}/system/financial/currency/delete/${currencyID}/`, {
      method: 'DELETE',
      'headers': {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer ' + authTokens?.access
      }
    })

    if (response.status === 200) {
      getCurrencyList()
      setOpenDeleteModal(false)
      let deleteSuccessMessage = "System currency (#" + currencyID + ") has been delete."
      showToast("success", "Success", deleteSuccessMessage)
      setError(false)
    } else {
      setError(true)
    }
  }

  const currenciesHeaders = [
    { label: "Currency Description", key: "currency" },
    { label: "Currency Code", key: "currencycode" }
  ];

  const currencyData = useMemo(() => {
    let computedCurrencies = search(currencies);
    setTotalItems(computedCurrencies.length);
    //Current Page slice
    return computedCurrencies.slice(
      firstRowIndex,
      firstRowIndex + currentPageSize
    );
  }, [currencies, firstRowIndex, currentPageSize, currencySearch]);

  const csvCurrencyReport = {
    data: currencies,
    headers: currenciesHeaders,
    filename: `WillowBMS_system_currencies.csv`
  };

  function search(rows) {
    return rows.filter(
      (row) =>
        row.currency.toLowerCase().indexOf(currencySearch) > -1 ||
        row.currency.indexOf(currencySearch) > -1 ||
        row.currencycode.indexOf(currencySearch) > -1 ||
        row.currencycode.toLowerCase().indexOf(currencySearch) > -1
    );
  }

  return (
    <div id="body" className="bms-page d-flex">
      <Toast toastList={list} />
      <div className="wrapper">
        <Breadcrumb>
          <BreadcrumbItem href="/system/settings/general">Settings</BreadcrumbItem>
          <BreadcrumbItem href="/system/settings/financial">Financial</BreadcrumbItem>
          <span>Currencies</span>
        </Breadcrumb>
        <h2>
          Financial
        </h2>
        <Tile light={true} className="row mt-1">
          <h5 className="tile-title mb-1">Currencies</h5>
          <CurrenciesList setDeleteID={setDeleteID} csvCurrencyReport={csvCurrencyReport} setCurrencySearch={setCurrencySearch} deleteID={deleteID} batchDelete={batchDelete} openDeleteModal={openDeleteModal} setOpenDeleteModal={setOpenDeleteModal} editID={editID} editSetup={editSetup} editForm={editForm} openEditModal={openEditModal} setOpenEditModal={setOpenEditModal} editCurrency={editCurrency} addCurrency={addCurrency} addForm={addForm} openAddModal={openAddModal} setOpenAddModal={setOpenAddModal} error={error} setError={setError} rows={currencyData} totalItems={totalItems} currentPageSize={currentPageSize} setCurrentPageSize={setCurrentPageSize} setFirstRowIndex={setFirstRowIndex} />
        </Tile>
      </div>
    </div>
  )
}

export default CurrenciesSettings